export const LEGACY_SYSTEM_SETTINGS_STORAGE_KEY = 'nordrevived.system-settings.v1';

export const LEGACY_SHADOW_QUALITIES = ['off', 'low', 'medium', 'high', 'extreme'] as const;
export const LIGHTING_MODES = ['classic', 'enhanced'] as const;
export const LEGACY_SOUND_VOLUMES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;

export type LegacyShadowQuality = (typeof LEGACY_SHADOW_QUALITIES)[number];
export type LightingMode = (typeof LIGHTING_MODES)[number];
export type LegacySoundVolume = (typeof LEGACY_SOUND_VOLUMES)[number];

export type LegacySystemSettings = Readonly<{
  lightingMode: LightingMode;
  disableVillageLighting: boolean;
  highQuality: boolean;
  shadowQuality: LegacyShadowQuality;
  bloom: boolean;
  rotateWithCursor: boolean;
  firstPersonMode: boolean;
  horseCameraBounce: boolean;
  disableResourceSpinners: boolean;
  hideSpeechBubbles: boolean;
  hideLiveMessages: boolean;
  wasdSteering: boolean;
  sound: boolean;
  extraSoundEffects: boolean;
  soundVolume: LegacySoundVolume;
  backgroundMusic: boolean;
  musicVolume: LegacySoundVolume;
}>;

export const DEFAULT_LEGACY_SYSTEM_SETTINGS: LegacySystemSettings = Object.freeze({
  lightingMode: 'classic',
  disableVillageLighting: false,
  highQuality: true,
  shadowQuality: 'high',
  bloom: true,
  rotateWithCursor: false,
  firstPersonMode: false,
  horseCameraBounce: true,
  disableResourceSpinners: false,
  hideSpeechBubbles: false,
  hideLiveMessages: false,
  wasdSteering: true,
  sound: true,
  extraSoundEffects: true,
  soundVolume: 100,
  backgroundMusic: false,
  musicVolume: 50,
});

export function loadLegacySystemSettings(
  storage: Pick<Storage, 'getItem'> = localStorage,
): LegacySystemSettings {
  try {
    const stored = storage.getItem(LEGACY_SYSTEM_SETTINGS_STORAGE_KEY);
    if (stored === null) return DEFAULT_LEGACY_SYSTEM_SETTINGS;
    return parseLegacySystemSettings(JSON.parse(stored));
  } catch {
    return DEFAULT_LEGACY_SYSTEM_SETTINGS;
  }
}

export function saveLegacySystemSettings(
  settings: LegacySystemSettings,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  storage.setItem(LEGACY_SYSTEM_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

export function saveLegacySoundVolume(
  soundVolume: LegacySoundVolume,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): void {
  saveLegacySystemSettings({ ...loadLegacySystemSettings(storage), soundVolume }, storage);
}

export function parseLegacySystemSettings(value: unknown): LegacySystemSettings {
  if (typeof value !== 'object' || value === null) return DEFAULT_LEGACY_SYSTEM_SETTINGS;
  const candidate = value as Record<string, unknown>;
  const shadowQuality = LEGACY_SHADOW_QUALITIES.find(
    (quality) => quality === candidate.shadowQuality,
  );
  const highQuality = booleanOrDefault(
    candidate.highQuality,
    DEFAULT_LEGACY_SYSTEM_SETTINGS.highQuality,
  );
  return {
    lightingMode:
      LIGHTING_MODES.find((mode) => mode === candidate.lightingMode) ??
      DEFAULT_LEGACY_SYSTEM_SETTINGS.lightingMode,
    disableVillageLighting: booleanOrDefault(
      candidate.disableVillageLighting,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.disableVillageLighting,
    ),
    highQuality,
    shadowQuality: highQuality
      ? (shadowQuality ?? DEFAULT_LEGACY_SYSTEM_SETTINGS.shadowQuality)
      : 'off',
    bloom: highQuality
      ? booleanOrDefault(candidate.bloom, DEFAULT_LEGACY_SYSTEM_SETTINGS.bloom)
      : false,
    rotateWithCursor: booleanOrDefault(
      candidate.rotateWithCursor,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.rotateWithCursor,
    ),
    firstPersonMode: booleanOrDefault(
      candidate.firstPersonMode,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.firstPersonMode,
    ),
    horseCameraBounce: booleanOrDefault(
      candidate.horseCameraBounce,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.horseCameraBounce,
    ),
    disableResourceSpinners: booleanOrDefault(
      candidate.disableResourceSpinners,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.disableResourceSpinners,
    ),
    hideSpeechBubbles: booleanOrDefault(
      candidate.hideSpeechBubbles,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.hideSpeechBubbles,
    ),
    hideLiveMessages: booleanOrDefault(
      candidate.hideLiveMessages,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.hideLiveMessages,
    ),
    wasdSteering: booleanOrDefault(
      candidate.wasdSteering,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.wasdSteering,
    ),
    sound: booleanOrDefault(candidate.sound, DEFAULT_LEGACY_SYSTEM_SETTINGS.sound),
    extraSoundEffects: booleanOrDefault(
      candidate.extraSoundEffects,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.extraSoundEffects,
    ),
    soundVolume:
      LEGACY_SOUND_VOLUMES.find((volume) => volume === candidate.soundVolume) ??
      DEFAULT_LEGACY_SYSTEM_SETTINGS.soundVolume,
    backgroundMusic: booleanOrDefault(
      candidate.backgroundMusic,
      DEFAULT_LEGACY_SYSTEM_SETTINGS.backgroundMusic,
    ),
    musicVolume:
      LEGACY_SOUND_VOLUMES.find((volume) => volume === candidate.musicVolume) ??
      DEFAULT_LEGACY_SYSTEM_SETTINGS.musicVolume,
  };
}

function booleanOrDefault(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
